<template>
  <div class="accreditations-container">
    <el-row class="mb-20">
      <el-col :span="24">
        <div v-if="!accreditations.length && !loading" class="empty-state">
          <i class="el-icon-document"></i>
          <p>No accreditations are available</p>
        </div>
        <el-table
          v-else
          :data="accreditations"
          row-key="_id"
          border
          stripe
          class="item-table"
          :fit="true"
        >
          <el-table-column type="expand" width="50">
            <template slot-scope="scope">
              <div class="expanded-details">
                <div class="info-section" v-if="scope.row.accreditation.additionalFields?.length">
                  <h4>Additional Fields</h4>
                  <div class="detail-item" v-for="field in scope.row.accreditation.additionalFields" :key="field.label">
                    <strong>{{ field.label }}:</strong>
                    <span>{{ scope.row.meta?.[field.label] || 'N/A' }}</span>
                  </div>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column
            prop="accreditation.name"
            label="Accreditation"
            min-width="150"
            sortable
          >
            <template slot-scope="scope">
              <div class="break-word">{{ scope.row.accreditation.name }}</div>
            </template>
          </el-table-column>

          <el-table-column
            label="Status"
            width="120"
            align="center"
          >
            <template slot-scope="scope">
              <div v-if="!scope.row.isActive" class="inactive-badge">Inactive</div>
              <div v-else class="active-badge">Active</div>
            </template>
          </el-table-column>

          <el-table-column
            prop="accreditation.description"
            label="Description"
            min-width="200"
            show-overflow-tooltip
          >
            <template slot-scope="scope">
              <div class="description-text break-word">
                {{ scope.row.accreditation.description }}
              </div>
            </template>
          </el-table-column>

          <el-table-column
            prop="expiry"
            label="Expiry"
            width="120"
            sortable
          >
            <template slot-scope="scope">
              {{ moment(scope.row.expiry).format('YYYY-MM-DD') }}
            </template>
          </el-table-column>

          <el-table-column
            prop="createdAt"
            label="Date Issued"
            width="130"
            sortable
          >
            <template slot-scope="scope">
              {{ moment(scope.row.createdAt).format('YYYY-MM-DD') }}
            </template>
          </el-table-column>

          <el-table-column
            width="60"
            align="center"
            fixed="right"
          >
            <template slot-scope="scope">
              <el-dropdown trigger="click" size="small">
                <i class="el-icon-more action-icon"></i>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item @click.native="openDialog(scope.row)">
                    <i class="el-icon-document"></i> View Documents
                  </el-dropdown-item>
                  <el-dropdown-item @click.native="openMetaDialog(scope.row)">
                    <i class="el-icon-edit"></i> Update Fields
                  </el-dropdown-item>
                  <el-dropdown-item divided @click.native="downloadAccreditationCard({value: scope.row, type:'pdf'})">
                    <i class="el-icon-document"></i> Download PDF Card
                  </el-dropdown-item>
                  <el-dropdown-item @click.native="downloadAccreditationCard({value: scope.row, type:'png'})">
                    <i class="el-icon-picture"></i> Download PNG Card
                  </el-dropdown-item>
                  <el-dropdown-item @click.native="downloadAccreditationCard({value: scope.row, type:'qr-code'})">
                    <i class="el-icon-postcard"></i> View QR Code
                  </el-dropdown-item>
                  <el-dropdown-item @click.native="downloadAccreditationCard({value: scope.row, type:'regenerate'})">
                    <i class="el-icon-refresh"></i> Regenerate Card
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </template>
          </el-table-column>
        </el-table>
      </el-col>
    </el-row>

    <el-dialog :visible.sync="dialogVisible" width="100%">
      <accreditation-files
        :value="selectedAccreditation"
        :current-member="member"
        @close="dialogVisible = false"
      />
    </el-dialog>

    <el-dialog
      :title="`Qr Code for accreditation card`"
      :visible.sync="showQrCodeDialog"
      width="30%"
      >
      <div style="overflow-x: auto; width:100%">
        <div class="d-flex justify-content-center">
          <img :src="qrCode" alt="QR Code" />
        </div>
      </div>
    </el-dialog>

    <el-dialog
      :visible.sync="metaDialogVisible"
      :title="`Update Fields - ${selectedAccreditation?.accreditation?.name || ''}`"
      width="50%"
      :close-on-click-modal="false"
    >
      <el-form
        ref="metaForm"
        :model="selectedAccreditation"
        label-position="top"
        v-if="selectedAccreditation"
      >
        <el-row :gutter="20">
          <el-col
            v-for="field in selectedAccreditation.accreditation.additionalFields"
            :key="field.label"
            :xs="24"
            :sm="field.type === 'boolean' ? 12 : 24"
            :md="field.type === 'boolean' ? 12 : 12"
          >
            <el-form-item
              :label="field.label"
            >
              <!-- Initialize meta object if it doesn't exist -->
              <template v-if="!selectedAccreditation.meta">
                {{ $set(selectedAccreditation, 'meta', {}) }}
              </template>

              <el-input
                v-if="field.type === 'text'"
                v-model="selectedAccreditation.meta[field.label]"
                :placeholder="`Enter ${field.label}`"
                @change="updateAccreditationMeta(selectedAccreditation)"
              />

              <el-input-number
                v-else-if="field.type === 'number'"
                v-model="selectedAccreditation.meta[field.label]"
                :controls="true"
                :placeholder="`Enter ${field.label}`"
                @change="updateAccreditationMeta(selectedAccreditation)"
                style="width: 100%"
              />

              <el-radio-group
                v-else-if="field.type === 'boolean'"
                v-model="selectedAccreditation.meta[field.label]"
                @change="updateAccreditationMeta(selectedAccreditation)"
              >
                <el-radio :label="true">Yes</el-radio>
                <el-radio :label="false">No</el-radio>
              </el-radio-group>

              <el-date-picker
                v-else-if="field.type === 'date'"
                v-model="selectedAccreditation.meta[field.label]"
                type="date"
                value-format="YYYY-MM-DD"
                :placeholder="`Select ${field.label}`"
                @change="updateAccreditationMeta(selectedAccreditation)"
                style="width: 100%"
              />

              <el-select
                v-else-if="field.type === 'select'"
                v-model="selectedAccreditation.meta[field.label]"
                :placeholder="`Select ${field.label}`"
                @change="updateAccreditationMeta(selectedAccreditation)"
                style="width: 100%"
              >
                <el-option
                  v-for="option in field.options"
                  :key="option.value"
                  :label="option.label"
                  :value="option.value"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="metaDialogVisible = false">Cancel</el-button>
        <el-button type="primary" @click="closeMetaDialog">Confirm</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import _, { capitalize } from 'lodash';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts.js';
import moment from 'moment';
import AccreditationFiles from './AccreditationFiles.vue';

export default {
  props: ['member'],
  data() {
    return {
      capitalize,
      accreditations: [],
      dialogVisible: false,
      selectedAccreditation: null,
      showQrCodeDialog: false,
      qrCode: null,
      metaDialogVisible: false,
      isExpanded: {},
      msCard: null,
      loading: false,
    };
  },
  components: {
    AccreditationFiles
  },
  mounted() {
    if (this.member && this.member._id) {
      this.fetchAccreditations();
    }
  },
  methods: {
    moment,
    fetchAccreditations() {
      this.loading = true;
      this.$store.commit('loading', { loading: true, text: 'Loading...' });
      this.$http
        .get(
          `/assemble/api/v1/portal/members/${this.member._id}/accreditations`
        )
        .then((res) => {
          console.log('API response:', res.data);
          this.accreditations = res.data.data.map((accreditation) => {
            // Create new object to avoid modifying the parameter
            const processedAccreditation = { ...accreditation };

            // Initialize meta if it doesn't exist
            if (!processedAccreditation.meta) {
              this.$set(processedAccreditation, 'meta', {});
            }

            // Make sure accreditation.accreditation exists
            if (!processedAccreditation.accreditation) {
              processedAccreditation.accreditation = {
                name: 'Unknown',
                description: '',
                additionalFields: []
              };
            }

            return processedAccreditation;
          });
          console.log('Processed accreditations:', this.accreditations);
          this.$store.commit('loading', false);
        })
        .catch((e) => {
          console.error('Error fetching accreditations:', e);
          this.$message.error(e.response?.data?.message || 'Error loading accreditations');
          this.$store.commit('loading', false);
        });
    },
    shouldShowExpand(text) {
      const lineHeight = 20; // Approximate line height in pixels
      const maxHeight = lineHeight * 2; // Two lines
      const tempEl = document.createElement('div');
      tempEl.style.width = '100%';
      tempEl.style.position = 'absolute';
      tempEl.style.visibility = 'hidden';
      tempEl.style.fontSize = '0.9em';
      tempEl.innerText = text;
      document.body.appendChild(tempEl);
      const height = tempEl.offsetHeight;
      document.body.removeChild(tempEl);
      return height > maxHeight;
    },
    toggleExpand(id) {
      this.$set(this.isExpanded, id, !this.isExpanded[id]);
    },
    openMetaDialog(accreditation) {
      this.selectedAccreditation = accreditation;
      if (accreditation.accreditation.additionalFields?.length) {
        this.metaDialogVisible = true;
      } else {
        this.$message.error('No additional fields to update');
      }
    },

    closeMetaDialog() {
      this.metaDialogVisible = false;
      this.selectedAccreditation = null;
    },
    async updateAccreditationMeta(accreditation) {
      try {
        this.$store.commit('loading', true);
        await this.$http.put(
          `/assemble/api/v1/portal/accreditations/${accreditation._id}/meta`,
          { meta: accreditation.meta }
        );
        this.$message.success('Additional fields updated successfully');

        // If you have an accreditation card that needs to be regenerated
        // after meta updates, you can trigger that here
        this.handleAccreditationCardNotFound(
          { value: accreditation, type: 'update' },
          'update',
          true
        );
      } catch (error) {
        this.$message.error(error.response?.data?.message || 'Failed to update additional fields');
      } finally {
        this.$store.commit('loading', false);
      }
    },
    parseDate(dateStr) {
      if (!dateStr) return null;
      // Convert from DD/MM/YYYY to Date object
      const [day, month, year] = dateStr.split('/');
      return new Date(year, month - 1, day);
    },

    updateDate(row, fieldLabel, date) {
    // Convert Date object to DD/MM/YYYY string
      const formattedDate = date ? moment(date).format('DD/MM/YYYY') : null;
      this.$set(row.meta, fieldLabel, formattedDate);
      this.updateAccreditationMeta(row);
    },
    isTimestamp(value) {
      // Check if the value is a number and has 13 digits (milliseconds timestamp)
      // or 10 digits (seconds timestamp)
      return typeof value === 'number'
        && (String(value).length === 13 || String(value).length === 10);
    },

    formatTimestamp(timestamp) {
      // Convert to milliseconds if it's in seconds
      const ms = String(timestamp).length === 10 ? timestamp * 1000 : timestamp;
      return moment(ms).format('YYYY-MM-DD HH:mm:ss');
    },
    openDialog(accreditation) {
      this.selectedAccreditation = accreditation;
      this.dialogVisible = true;
    },
    splitTextIntoLines(ctx, text, maxWidth) {
      if (!text) return [''];

      const words = text.split(' ');
      const lines = [];
      let currentLine = words[0] || '';

      for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const { width } = ctx.measureText(`${currentLine} ${word}`);

        if (width < maxWidth) {
          currentLine += ` ${word}`;
        } else {
          lines.push(currentLine);
          currentLine = word;
        }
      }

      lines.push(currentLine);
      return lines;
    },
    async generateMSCard(membershipDetails, memberProfilePics, entityPic) {
      const cardCanvas = document.createElement('canvas');
      cardCanvas.width = 500;
      cardCanvas.height = 300;
      const cardWidth = cardCanvas.width;
      const pageHeight = cardCanvas.height;
      const cardContext = cardCanvas.getContext('2d');
      const lineSpacing = 22;
      const columnSpacing = 260;
      // const lineWidth = 2;
      let isLogoURL = false;
      let isProfileURL = false;
      let isEntityURL = false;

      // Modern color scheme
      const colors = {
        primary: '#1a237e', // Deep blue for primary elements
        secondary: '#303F9F', // Lighter blue for secondary elements
        text: '#2c3e50', // Dark blue-grey for text
        lightText: '#5c6b7f', // Lighter text color
        accent: '#3949ab', // Accent color for highlights
        divider: '#e0e6ed', // Light grey for dividers
        background: '#ffffff' // White background
      };

      let logoImage;
      let profileImage;
      let entityImage;
      let logoPromise;
      let profilePromise;
      let entityPromise;
      const dobFormat = +process.env.VUE_APP_NATIONAL_ID === 15 ? 'YYYY' : 'DD/MM/YYYY'; // Add this line

      // Load Logo Image
      if (process.env.VUE_APP_LOGO && process.env.VUE_APP_LOGO !== '') {
        isLogoURL = true;
      }
      if (memberProfilePics && memberProfilePics !== '') {
        isProfileURL = true;
      }
      if (entityPic && entityPic !== '') {
        isEntityURL = true;
      }

      const additionalFields = membershipDetails.additionalFields || [];
      console.log('additionalFields', additionalFields);

      const memberMeta = membershipDetails.memberMeta || {};

      if (isLogoURL) {
        const logoContent = await this.$http.post('/assemble/api/v1/portal/registrations/image-content', { logo: process.env.VUE_APP_LOGO, isURL: true });
        logoImage = new Image();
        logoImage.src = logoContent.data.data.imageContent;
        // eslint-disable-next-line no-return-assign
        logoPromise = new Promise((resolve) => logoImage.onload = resolve);
      }
      if (isProfileURL) {
        const profileContent = await this.$http.post('/assemble/api/v1/portal/registrations/image-content', { memberProfile: memberProfilePics, isURL: true });
        profileImage = new Image();
        profileImage.src = profileContent.data.data.memberImageContent;
        // eslint-disable-next-line no-return-assign
        profilePromise = new Promise((resolve) => profileImage.onload = resolve);
      }
      if (isEntityURL) {
        const entityContent = await this.$http.post('/assemble/api/v1/portal/registrations/image-content', { entityLogo: entityPic, isURL: true });
        entityImage = new Image();
        entityImage.src = entityContent.data.data.entityImageContent;
        // eslint-disable-next-line no-return-assign
        entityPromise = new Promise((resolve) => entityImage.onload = resolve);
      }

      await Promise.all([logoPromise, profilePromise, entityPromise].filter((p) => p));

      // Draw card background with subtle gradient
      const gradient = cardContext.createLinearGradient(0, 0, cardWidth, pageHeight);
      gradient.addColorStop(0, colors.background);
      gradient.addColorStop(1, '#f8f9fa');

      cardContext.fillStyle = gradient;
      cardContext.beginPath();
      // Increased border radius for modern look
      const borderRadius = 25;
      cardContext.moveTo(borderRadius, 0);
      cardContext.lineTo(cardWidth - borderRadius, 0);
      cardContext.quadraticCurveTo(cardWidth, 0, cardWidth, borderRadius);
      cardContext.lineTo(cardWidth, pageHeight - borderRadius);
      cardContext.quadraticCurveTo(cardWidth, pageHeight, cardWidth - borderRadius, pageHeight);
      cardContext.lineTo(borderRadius, pageHeight);
      cardContext.quadraticCurveTo(0, pageHeight, 0, pageHeight - borderRadius);
      cardContext.lineTo(0, borderRadius);
      cardContext.quadraticCurveTo(0, 0, borderRadius, 0);
      cardContext.closePath();
      cardContext.fill();

      // Add subtle shadow effect
      cardContext.beginPath();
      cardContext.moveTo(borderRadius, 0);
      cardContext.lineTo(cardWidth - borderRadius, 0);
      cardContext.quadraticCurveTo(cardWidth, 0, cardWidth, borderRadius);
      cardContext.lineTo(cardWidth, pageHeight - borderRadius);
      cardContext.quadraticCurveTo(cardWidth, pageHeight, cardWidth - borderRadius, pageHeight);
      cardContext.lineTo(borderRadius, pageHeight);
      cardContext.quadraticCurveTo(0, pageHeight, 0, pageHeight - borderRadius);
      cardContext.lineTo(0, borderRadius);
      cardContext.quadraticCurveTo(0, 0, borderRadius, 0);
      cardContext.closePath();
      cardContext.strokeStyle = colors.primary; // or '#000000' for black border
      cardContext.lineWidth = 2;
      cardContext.stroke();

      cardContext.imageSmoothingEnabled = true;
      cardContext.imageSmoothingQuality = 'high';

      // Draw member profile logo
      const profileLogoWidth = 100;
      const profileLogoHeight = 100;
      const profileLogoX = 10;
      const profileLogoY = 10;
      cardContext.globalAlpha = 1;
      if (profileImage) {
        cardContext.drawImage(profileImage, profileLogoX, profileLogoY, profileLogoWidth, profileLogoHeight);
      } else {
        cardContext.fillStyle = '#f5f5f5';
        cardContext.fillRect(profileLogoX, profileLogoY, profileLogoWidth, profileLogoHeight);
      }

      // Draw entity logo
      const entityLogoWidth = 100;
      const entityLogoHeight = 100;
      const entityLogoX = cardWidth - entityLogoWidth - 10;
      const entityLogoY = 10;

      if (entityImage) {
        cardContext.drawImage(entityImage, entityLogoX, entityLogoY, entityLogoWidth, entityLogoHeight);
      } else if (logoImage) {
        cardContext.drawImage(logoImage, entityLogoX, entityLogoY, entityLogoWidth, entityLogoHeight);
      }

      // Draw entity text
      const maxLineWidth = 260;
      cardContext.font = 'bold 22px "Helvetica Neue", Arial, sans-serif';
      cardContext.fillStyle = colors.primary;
      const textStartX = profileLogoX + profileLogoWidth + 10;
      const entityTextY = profileLogoY + 20;

      const firstLevelLines = this.splitTextIntoLines(cardContext, membershipDetails.entityCreatedFirstLevelLimited, maxLineWidth);
      firstLevelLines.slice(0, 2).forEach((line, index) => {
        cardContext.fillText(line, textStartX, entityTextY + (index * 25));
      });

      const secondLevelStartY = entityTextY + (firstLevelLines.length * 25) + 5;
      cardContext.font = '16px "Helvetica Neue", Arial, sans-serif';
      cardContext.fillStyle = colors.lightText;

      if (membershipDetails.entityCreatedSecondLevelLimited) {
        const secondLevelLines = this.splitTextIntoLines(cardContext, membershipDetails.entityCreatedSecondLevelLimited, maxLineWidth);
        secondLevelLines.slice(0, 2).forEach((line, index) => {
          cardContext.fillText(line, textStartX, secondLevelStartY + (index * 20));
        });
      }

      // Draw accreditation name and description
      const accreditationY = pageHeight - 165;
      cardContext.fillStyle = colors.primary;
      cardContext.font = 'bold 22px "Helvetica Neue", Arial, sans-serif';
      const accreditationNameLines = this.splitTextIntoLines(cardContext, membershipDetails.accreditationName, 450);
      accreditationNameLines.forEach((line, index) => {
        cardContext.fillText(line, 10, accreditationY + index * lineSpacing);
      });

      // Add description
      cardContext.font = '16px "Helvetica Neue", Arial, sans-serif';
      cardContext.fillStyle = colors.lightText;
      const description = membershipDetails.description || '';
      const truncatedDescription = description.length > 60 ? `${description.substring(0, 57)}...` : description;
      cardContext.fillText(truncatedDescription, 10, accreditationY + lineSpacing);

      // Draw sections with modern styling
      const sectionHeaderY = pageHeight - 100;
      cardContext.font = '600 18px "Helvetica Neue", Arial, sans-serif';
      cardContext.fillStyle = colors.primary;
      cardContext.fillText('Accreditation Details', 10, sectionHeaderY);
      cardContext.fillText('Member Details', columnSpacing, sectionHeaderY);

      // Add subtle section dividers
      cardContext.beginPath();
      cardContext.moveTo(10, sectionHeaderY + 5);
      cardContext.lineTo(240, sectionHeaderY + 5);
      cardContext.moveTo(columnSpacing, sectionHeaderY + 5);
      cardContext.lineTo(cardWidth - 10, sectionHeaderY + 5);
      cardContext.strokeStyle = colors.divider;
      cardContext.lineWidth = 2;
      cardContext.stroke();

      // Draw details
      const detailsStartY = sectionHeaderY + lineSpacing;
      cardContext.font = '16px "Helvetica Neue", Arial, sans-serif';

      // First column - Accreditation Details
      cardContext.fillStyle = colors.lightText;
      cardContext.fillText('Expiry:', 10, detailsStartY);
      cardContext.fillStyle = colors.text;
      cardContext.fillText(membershipDetails.expiry, 10 + cardContext.measureText('Expiry: ').width, detailsStartY);

      // Additional fields
      let currentDetailY = detailsStartY + lineSpacing;
      additionalFields.forEach((field) => {
        if (field.displayOnCard) {
          const fieldValue = memberMeta[field.name] || memberMeta[field.label] || '';
          if (currentDetailY < pageHeight - 30) {
            cardContext.fillStyle = colors.lightText;
            cardContext.fillText(`${field.label}:`, 10, currentDetailY);
            cardContext.fillStyle = colors.text;
            cardContext.fillText(fieldValue.toString(), 10 + cardContext.measureText(`${field.label}: `).width, currentDetailY);
            currentDetailY += lineSpacing;
          }
        }
      });

      // Second column - Member Details
      const memberDetails = [
        { label: 'Name', value: membershipDetails.memberName },
        { label: 'Member ID', value: this.member._id },
        { label: 'Member Number', value: membershipDetails.memberNumber },
        {
          label: dobFormat === 'YYYY' ? 'Year of Birth' : 'DOB',
          value: moment(membershipDetails.memberDob).format(dobFormat)
        }
      ];

      memberDetails.forEach((detail, index) => {
        cardContext.fillStyle = colors.lightText;
        cardContext.fillText(`${detail.label}:`, columnSpacing, detailsStartY + index * lineSpacing);
        cardContext.fillStyle = colors.text;
        cardContext.fillText(detail.value,
          columnSpacing + cardContext.measureText(`${detail.label}: `).width,
          detailsStartY + index * lineSpacing);
      });

      // Vertical divider with gradient
      const dividerGradient = cardContext.createLinearGradient(0, sectionHeaderY, 0, currentDetailY);
      dividerGradient.addColorStop(0, colors.divider);
      dividerGradient.addColorStop(1, '#ffffff00');
      cardContext.beginPath();
      cardContext.moveTo(columnSpacing - 20, sectionHeaderY);
      cardContext.lineTo(columnSpacing - 20, currentDetailY);
      cardContext.strokeStyle = dividerGradient;
      cardContext.lineWidth = 1;
      cardContext.stroke();

      // Add subtle watermark
      if (logoImage) {
        cardContext.globalAlpha = 0.03;
        cardContext.drawImage(logoImage, 0, 0, cardWidth, pageHeight);
        cardContext.globalAlpha = 1;
      }

      return new Promise((resolve) => {
        cardCanvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/png');
      });
    },
    handleAccreditationCardNotFound(val, action, change) {
      const { value: accreditation } = val;
      let msCard;
      let confirmText = 'Accreditation card is not available yet. Do you want to generate it now?';
      if (change) {
        confirmText = 'Accreditation details have been updated. Select yes to generate a new Accreditation Card.';
      }
      if (action === 'regenerate') {
        confirmText = 'Do you want to regenerate the Accreditation Card?';
      }
      this.$confirm(confirmText, 'Confirm', {
        distinguishCancelAndClose: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
      })
        .then(async () => {
          console.log('accreditationss', accreditation);
          const currMember = this.member;
          const currentSeason = this.$store.getters['season/currentSeason'] || '';
          const currMemberProfilePic = _.get(currMember, 'meta.imgUrl');
          const entityPic = _.get(accreditation, 'regoEntity.meta.imgUrl');
          const { orgtree } = accreditation.accreditation;
          console.log('orgtree', orgtree);
          console.log('currMember', currMember);

          // Manually create an array in the desired order from lowest to highest
          const orderedOrgtree = [
            orgtree?.club, // Start with the lowest level
            orgtree?.association,
            orgtree?.state,
            orgtree?.national
          ].filter((org) => org !== undefined); // This will remove any undefined levels, in case some levels do not exist
          console.log('orderedOrgtree', orderedOrgtree);

          const entityCreatedFirstLevel = orderedOrgtree[0] ? orderedOrgtree[0].name : '';
          const entityCreatedSecondLevel = orderedOrgtree[1] ? orderedOrgtree[1].name : '';
          const entityCreatedFirstLevelLimited = entityCreatedFirstLevel.length > 67 ? `${entityCreatedFirstLevel.substring(0, 67)}...` : entityCreatedFirstLevel;
          const entityCreatedSecondLevelLimited = entityCreatedSecondLevel.length > 77 ? `${entityCreatedSecondLevel.substring(0, 77)}...` : entityCreatedSecondLevel;

          // Reverse the array to start from club and then move up, and take the first two entities
          // const entityCreated = orderedOrgtree.slice(0, 2).map((org) => org.name).join(', ');
          // console.log('entityCreated length', entityCreated.length);
          // const entityCreatedLimited = entityCreated.length > 45 ? `${entityCreated.substring(0, 45)}...` : entityCreated;
          const accreditationDetails = {
            accreditationName: accreditation.fromImport ? 'Legacy Accreditation' : accreditation.accreditation.name,
            accreditationId: accreditation.fromImport ? '0' : accreditation._id,
            expiry: moment(_.get(accreditation, 'expiry')).format('DD/MM/YYYY'),
            memberType: _.capitalize(accreditation.memberType),
            season: currentSeason,
            memberId: this.member._id,
            memberName: `${currMember.profile.firstName} ${currMember.profile.lastName}`,
            memberDob: currMember.profile.dob,
            memberNumber: _.get(currMember, 'meta.memberNumber', ''),
            entityCreatedFirstLevelLimited,
            entityCreatedSecondLevelLimited,
            deregistered: accreditation.deregistered,
            expired: accreditation.expired,
            additionalFields: accreditation.accreditation.additionalFields,
            memberMeta: accreditation.meta,
            description: accreditation.accreditation.description,
          };
          console.log('accreditationDetails', accreditationDetails);

          this.$store.commit('loading', true);
          msCard = await this.generateMSCard(accreditationDetails, currMemberProfilePic, entityPic);
          console.log('card', msCard);
          console.log('accreditationDetails', accreditationDetails);
          // get presigned url
          const presignedUrl = await this.$http.post('/assemble/api/v1/portal/accreditation-card/presigned-url',
            {
              fileName: `accreditation-card-${accreditation._id}-${this.member._id}.png`,
              folder: 'accreditation-cards',
              accreditationDetails,
              accreditationId: accreditation._id,
              regenerating: !!change,
            });

          const { url, s3Path } = presignedUrl.data.data;
          this.$store.commit('loading', false);

          if (s3Path) {
            _.assign(accreditation, { accreditationCard: s3Path });
          }

          await fetch(url, {
            method: 'PUT',
            body: msCard,
            headers: { 'Content-Type': '*/*' },
          });
          this.$message.success('Membership card generated successfully');
          if (action === 'showQRCode') {
            const findMsCard = await this.$http.get(`/assemble/api/v1/portal/accreditations/${accreditation._id}/members/${accreditation.member._id}/accreditation-card`);
            if (_.get(findMsCard, 'data.msCard.meta.qrCode')) {
              this.qrCode = findMsCard.data.msCard.meta.qrCode;
              this.showQrCodeDialog = true;
            }
          }
          if (msCard) {
            this.downloadAccreditationCard(val, true);
          }
        })
        .catch(async () => {
          if (change) {
            await this.downloadAccreditationCard(val, true);
          }
        });
    },
    dataURItoBlob(dataURI) {
      const byteString = atob(dataURI.split(',')[1]);
      const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);

      for (let i = 0; i < byteString.length; i += 1) {
        ia[i] = byteString.charCodeAt(i);
      }

      return new Blob([ab], { type: mimeString });
    },
    async getImageBase64(url) {
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        };
        img.src = url;
      });
    },
    async downloadAccreditationCard(val, skipCheck) {
      try {
        const { type, value: accreditation } = val;
        console.log('accreditation', accreditation);
        console.log('memberId', this.member._id);

        if (!(skipCheck === true)) {
          try {
            const msCardRes = await this.$http.get(`/assemble/api/v1/portal/accreditations/${accreditation._id}/members/${this.member._id}/accreditation-card`);
            this.msCard = msCardRes.data.data;
            const msCardDetails = msCardRes.data.msCard;
            console.log('msCardDetails', msCardDetails);
            if (msCardDetails?.meta?.qrCode) {
              this.qrCode = msCardDetails.meta.qrCode;
            }
            const hasMsCard = this.msCard && this.msCard !== '';
            if (!hasMsCard) {
              this.handleAccreditationCardNotFound(val);
              return;
            }
            if (type === 'regenerate') {
              this.handleAccreditationCardNotFound(val, 'regenerate');
              return;
            }
            if (msCardDetails) {
              // check meta field newChange
              if (msCardDetails.meta.newChange) {
                this.handleAccreditationCardNotFound(val, 'download', msCardDetails.meta.lastChange);
                return;
              }
            }
          } catch (error) {
            console.error('Error fetching card:', error);
            this.$message.error('Failed to fetch accreditation card');
            this.handleAccreditationCardNotFound(val);
            return;
          }
        }

        console.log('msCard', this.msCard);

        // msCard is in base64 format already
        if (type === 'png') {
          const link = document.createElement('a');
          link.download = `${accreditation._id}.png`;

          // Convert to blob with base64
          const blob = this.dataURItoBlob(this.msCard);
          const blobUrl = URL.createObjectURL(blob);
          // Set the link href to the blob url
          link.href = blobUrl;
          // Append the link to the body
          document.body.appendChild(link);
          // Trigger the click event on the link
          link.click();
        } else if (type === 'pdf') {
          pdfMake.vfs = pdfFonts.pdfMake.vfs;
          const dd = {
            content: [
              { image: this.msCard, width: [400], alignment: 'center' },
            ],
          };
          pdfMake.createPdf(dd).download(`${accreditation._id}.pdf`);
        } else if (type === 'qr-code') {
          this.showQrCodeDialog = true;
          console.log('this.msCard', this.msCard);
        }
      } catch (error) {
        console.error('Error handling card download:', error);
        this.$message.error('Failed to download accreditation card');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.accreditations-container {
  padding: 20px;
}

.mb-20 {
  margin-bottom: 20px;
}

.break-word {
  word-break: break-word;
}

/* Empty state styling */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background-color: #f9fafc;
  border: 1px dashed #d4d7dc;
  border-radius: 4px;
  margin-bottom: 20px;
}

.empty-state i {
  font-size: 48px;
  color: #909399;
  margin-bottom: 15px;
}

.empty-state p {
  font-size: 16px;
  color: #606266;
  margin: 0;
}

/* Status badges */
.inactive-badge, .active-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  padding: 2px 12px;
  border-radius: 6px;
  font-weight: 500;
  white-space: nowrap;
  min-width: 80px;
}

.inactive-badge {
  background-color: #ffebee;
  color: #f44336;
  border: 1px solid #f44336;
}

.active-badge {
  background-color: #e8f5e9;
  color: #4caf50;
  border: 1px solid #4caf50;
}

.description-text {
  max-height: 45px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  color: #606266;
  font-size: 13px;
}

/* Table styling */
::v-deep .item-table {
  border: 1px solid rgba(45, 58, 69, 0.1);
  width: 100%;
  table-layout: fixed;

  th {
    background: #f6f8fa !important;
    padding-right: 1em;
    text-align: left;
    height: 48px;
  }

  td {
    background-color: white !important;
    padding: 14px 8px;
    text-align: left;
  }
}

/* Expanded content styling */
.expanded-details {
  padding: 20px;
  background-color: #FAFBFC;
  margin: 0 20px;
  border-radius: 4px;
}

.info-section {
  margin-bottom: 15px;
}

.info-section h4 {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-top: 0;
  margin-bottom: 12px;
  border-bottom: 1px solid #EBEEF5;
  padding-bottom: 6px;
}

.detail-item {
  margin-bottom: 8px;
  display: flex;
  align-items: flex-start;
  line-height: 1.5;
}

.detail-item strong {
  min-width: 120px;
  display: inline-block;
  font-weight: 500;
  color: #606266;
}

/* Clean table styling from RegoHistory */
::v-deep .el-table {
  border: 1px solid #EBEEF5 !important;
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03) !important;
}

::v-deep .el-table th {
  background-color: #F5F7FA !important;
  font-weight: 500 !important;
  color: #606266 !important;
  border-bottom: 1px solid #EBEEF5 !important;
  padding: 10px 0 !important;
}

::v-deep .el-table td {
  border-bottom: 1px solid #EBEEF5 !important;
  padding: 14px 8px !important;
}

::v-deep .el-table td, ::v-deep .el-table th {
  border-right: 1px solid #EBEEF5 !important;
}

::v-deep .el-table td:last-child, ::v-deep .el-table th:last-child {
  border-right: none !important;
}

::v-deep .el-table tr:hover > td {
  background-color: #F5F7FA !important;
}

::v-deep .el-table--striped .el-table__body tr.el-table__row--striped td {
  background: #FAFBFC;
}

::v-deep .el-table__expanded-cell {
  padding: 15px 20px !important;
  background-color: #FAFBFC;
}

::v-deep .el-table__expand-icon {
  transition: transform 0.2s ease;
  margin-right: 5px;

  &.el-table__expand-icon--expanded {
    transform: rotate(90deg);
  }

  .el-icon {
    font-weight: bold;
    font-size: 14px;
    color: #409EFF;
  }
}

/* Fix table width on mobile and prevent horizontal scroll */
::v-deep .el-table__body {
  width: 100% !important;
}

::v-deep .el-table__header {
  width: 100% !important;
}

.d-flex {
  display: flex;
}

.justify-content-center {
  justify-content: center;
}

.action-icon {
  font-size: 20px;
  color: #909399;
  cursor: pointer;
  padding: 5px;
  transition: all 0.2s;
}

.action-icon:hover {
  color: #409EFF;
  transform: rotate(90deg);
}

@media (max-width: 768px) {
  .accreditations-container {
    padding: 10px;
  }

  /* Adjust badges for mobile */
  .inactive-badge, .active-badge {
    min-width: 70px;
    font-size: 12px;
  }

  /* Make sure expanded details fit on mobile */
  .expanded-details {
    padding: 15px 10px;
    margin: 0 10px;
  }

  .detail-item {
    flex-direction: column;
  }

  .detail-item strong {
    margin-bottom: 4px;
  }
}
</style>
